import React from 'react';

/* ------------------------------------------------------------------ */
/*  Primitives                                                         */
/* ------------------------------------------------------------------ */

export const SkeletonBlock = ({ className = '', ariaLabel = 'Loading', ...props }) => (
    <div
        className={`animate-pulse rounded-[20px] bg-gray-100 ${className}`}
        role="status"
        aria-label={ariaLabel}
        {...props}
    />
);

export const SkeletonCircle = ({ size = 40, className = '' }) => (
    <SkeletonBlock
        className={`rounded-full ${className}`}
        style={{ width: size, height: size }}
    />
);

export const SkeletonText = ({ lines = 3, className = '' }) => (
    <div className={`space-y-2 ${className}`} role="status" aria-label="Loading text">
        {Array.from({ length: lines }).map((_, i) => (
            <div
                key={i}
                className="animate-pulse rounded-[20px] bg-gray-100"
                style={{ height: 12, width: i === lines - 1 ? '60%' : '100%' }}
            />
        ))}
    </div>
);

/* ------------------------------------------------------------------ */
/*  Card                                                               */
/* ------------------------------------------------------------------ */

const SkeletonCard = ({ className = '', imageHeight = 'h-40' }) => (
    <div className={`rounded-[20px] border border-gray-200 bg-white p-5 ${className}`}>
        <SkeletonBlock className={`mb-4 w-full ${imageHeight}`} />
        <SkeletonText lines={3} />
    </div>
);

export const SkeletonCardGrid = ({ count = 6, cols = 3, imageHeight = 'h-40' }) => {
    const colClasses = {
        1: 'sm:grid-cols-1',
        2: 'sm:grid-cols-2',
        3: 'sm:grid-cols-2 xl:grid-cols-3',
        4: 'sm:grid-cols-2 xl:grid-cols-4',
    };
    return (
        <div className={`grid gap-6 ${colClasses[cols] || colClasses[3]}`} role="status" aria-label="Loading cards">
            {Array.from({ length: count }).map((_, i) => (
                <SkeletonCard key={i} imageHeight={imageHeight} />
            ))}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Dashboard / Stats                                                  */
/* ------------------------------------------------------------------ */

export const SkeletonStatCard = ({ className = '' }) => (
    <div className={`rounded-[20px] border border-gray-200 bg-white p-5 ${className}`}>
        <div className="flex items-start justify-between">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-8 w-8 rounded-lg" />
        </div>
        <SkeletonBlock className="mt-3 h-8 w-20" />
        <SkeletonBlock className="mt-2 h-3 w-16" />
    </div>
);

export const SkeletonDashboard = () => (
    <div className="space-y-6" role="status" aria-label="Loading dashboard">
        <div className="flex items-center justify-between">
            <div className="space-y-2">
                <SkeletonBlock className="h-5 w-48" />
                <SkeletonBlock className="h-3 w-32" />
            </div>
            <SkeletonBlock className="h-10 w-28 rounded-[20px]" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonStatCard key={i} />
            ))}
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Table                                                              */
/* ------------------------------------------------------------------ */

const SkeletonTableRow = ({ cells = 5 }) => (
    <tr className="border-b border-gray-50">
        {Array.from({ length: cells }).map((_, i) => (
            <td key={i} className="px-4 py-3">
                <SkeletonBlock className={i === 0 ? 'h-4 w-32' : i === cells - 1 ? 'h-4 w-20' : 'h-4 w-24'} />
            </td>
        ))}
    </tr>
);

export const SkeletonTable = ({ rows = 5, columns = 5, className = '' }) => (
    <div className={`space-y-4 ${className}`} role="status" aria-label="Loading table">
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
                <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                    {Array.from({ length: columns }).map((_, i) => (
                        <th key={i} scope="col" className="px-4 py-3">
                            <SkeletonBlock className="h-3 w-16" />
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {Array.from({ length: rows }).map((_, i) => (
                    <SkeletonTableRow key={i} cells={columns} />
                ))}
                </tbody>
            </table>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Form                                                               */
/* ------------------------------------------------------------------ */

const SkeletonInput = ({ className = '' }) => (
    <div className={`space-y-2 ${className}`}>
        <SkeletonBlock className="h-3 w-20" />
        <SkeletonBlock className="h-12 w-full" />
    </div>
);

const SkeletonTextarea = ({ className = '' }) => (
    <div className={`space-y-2 ${className}`}>
        <SkeletonBlock className="h-3 w-20" />
        <SkeletonBlock className="h-28 w-full" />
    </div>
);

export const SkeletonForm = ({ fields = 4, className = '' }) => (
    <div className={`space-y-5 ${className}`} role="status" aria-label="Loading form">
        {Array.from({ length: fields }).map((_, i) => (
            <SkeletonInput key={i} />
        ))}
        <SkeletonTextarea />
        <SkeletonBlock className="h-12 w-36" />
    </div>
);

/* ------------------------------------------------------------------ */
/*  Profile                                                            */
/* ------------------------------------------------------------------ */

export const SkeletonProfile = ({ className = '' }) => (
    <div className={`space-y-6 ${className}`} role="status" aria-label="Loading profile">
        <div className="flex items-center gap-5">
            <SkeletonCircle size={80} />
            <div className="space-y-2 flex-1">
                <SkeletonBlock className="h-5 w-40" />
                <SkeletonBlock className="h-3 w-24" />
            </div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonInput key={i} />
            ))}
        </div>
        <SkeletonTextarea />
        <SkeletonBlock className="h-12 w-36" />
    </div>
);

/* ------------------------------------------------------------------ */
/*  Detail Page (Trek / Blog)                                          */
/* ------------------------------------------------------------------ */

export const SkeletonDetail = ({ className = '' }) => (
    <div className={className} role="status" aria-label="Loading page">
        <SkeletonBlock className="h-[300px] sm:h-[400px] lg:h-[500px] w-full rounded-none" />
        <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-8 w-3/4" />
            <div className="flex gap-3">
                <SkeletonBlock className="h-6 w-20 rounded-[20px]" />
                <SkeletonBlock className="h-6 w-24 rounded-[20px]" />
            </div>
            <SkeletonText lines={8} />
            <div className="grid gap-5 sm:grid-cols-2">
                <SkeletonBlock className="h-32 w-full" />
                <SkeletonBlock className="h-32 w-full" />
            </div>
            <SkeletonText lines={6} />
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  List                                                               */
/* ------------------------------------------------------------------ */

const SkeletonListItem = ({ className = '' }) => (
    <div className={`flex items-center gap-4 ${className}`}>
        <SkeletonCircle size={40} />
        <div className="flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-32" />
            <SkeletonBlock className="h-3 w-24" />
        </div>
        <SkeletonBlock className="h-8 w-20 rounded-[20px]" />
    </div>
);

export const SkeletonList = ({ count = 5, className = '' }) => (
    <div className={`space-y-4 ${className}`} role="status" aria-label="Loading list">
        {Array.from({ length: count }).map((_, i) => (
            <SkeletonListItem key={i} />
        ))}
    </div>
);

/* ------------------------------------------------------------------ */
/*  Chart / Analytics                                                  */
/* ------------------------------------------------------------------ */

export const SkeletonChart = ({ className = '' }) => (
    <div className={`space-y-4 ${className}`} role="status" aria-label="Loading chart">
        <div className="flex items-center justify-between">
            <SkeletonBlock className="h-5 w-40" />
            <SkeletonBlock className="h-8 w-24 rounded-[20px]" />
        </div>
        <div className="flex items-end gap-2" style={{ height: 200 }}>
            {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonBlock
                    key={i}
                    className="flex-1"
                    style={{ height: `${30 + Math.random() * 70}%` }}
                />
            ))}
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Weather                                                            */
/* ------------------------------------------------------------------ */

export const SkeletonWeather = ({ className = '' }) => (
    <div className={`flex items-center gap-4 ${className}`} role="status" aria-label="Loading weather">
        <SkeletonCircle size={40} />
        <div className="space-y-1.5">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-3 w-16" />
        </div>
    </div>
);
